import React, { ElementType, FC, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ColorError,
  ColorInfo,
  ColorPrimary,
  ColorSecondary,
  ColorSuccess,
  ColorWarning,
  ErrorIcon,
  IgCloseButton,
  IgTypography,
  InfoIcon,
  SuccessIcon,
  WarningIcon,
} from "@intelligems/ig-theme";
import { Box, Grid } from "@mui/material";
import { AlertColor } from "@mui/material/Alert/Alert";
import { Theme } from "@mui/material/styles";
import { SxProps } from "@mui/system";

import { useGetOrg } from "../../api/organizations/get-organization";
import {
  dismissAlert,
  getIsAlertDismissed,
} from "../../store/slices/appSettings";

import { HiddenErrorBoundary } from "./IgErrorBoundary";

interface IgAlertProps {
  dismissId?: string;
  severity: AlertColor;
  loading?: boolean;
  loadingPercent?: number;
  title: ReactNode | string;
  hideDismiss?: boolean;
  message?: ReactNode | string;
  longMessage?: ReactNode | string;
  buttons?: ReactNode[];
  sx?: SxProps<Theme>;
  variant?: "compact" | "regular";
}

const IconMap: Record<AlertColor, ElementType> = {
  error: ErrorIcon,
  warning: WarningIcon,
  success: SuccessIcon,
  info: InfoIcon,
};

export const ColorMap: Record<AlertColor, string> = {
  error: ColorError.main,
  warning: ColorWarning.dark,
  info: ColorPrimary.main,
  success: ColorSuccess.main,
};

// Since this component needs access to redux we can't add to ig-theme

export const IgAlert: FC<IgAlertProps> = ({
  severity,
  dismissId,
  title,
  loadingPercent,
  loading,
  message,
  longMessage,
  buttons,
  sx,
  hideDismiss,
}) => {
  const Icon: ElementType = IconMap[severity];
  const dispatch = useDispatch();
  const org = useGetOrg();
  const isDismissed = useSelector(
    getIsAlertDismissed(org.data?.org.id, dismissId)
  );

  if (isDismissed) {
    return null;
  }

  return (
    <HiddenErrorBoundary>
      <Grid
        direction={"column"}
        container
        item
        sx={{
          minHeight: "64px",
          overflow: "hidden",
          border: `1px solid ${ColorMap[severity]}`,
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "11px 8px 0px 20px",
          flexWrap: "nowrap",
          ...sx,
        }}
      >
        <Grid
          container
          item
          direction={"row"}
          justifyContent={"space-between"}
          sx={{
            minHeight: "40px",
            flexWrap: "nowrap",
          }}
        >
          <Grid
            direction={"column"}
            container
            item
            columnSpacing={0}
            sx={{ flexWrap: "nowrap", marginTop: "auto", marginBottom: "auto" }}
            flexGrow={8}
          >
            <Grid
              container
              item
              sx={{
                marginTop: "auto !important",
                marginBottom: "auto !important",
              }}
            >
              <Grid
                item
                sx={{
                  marginRight: "10px",
                  marginTop: "4px",
                  marginBottom: "auto",
                }}
              >
                <Icon width={"22"} height={"22"} />
              </Grid>
              <Grid
                item
                sx={{
                  marginRight: "5px",
                  marginTop: "auto !important",
                  marginBottom: "auto !important",
                }}
              >
                {typeof title === "string" ? (
                  <IgTypography
                    sx={{ color: ColorMap[severity], fontWeight: "400" }}
                  >
                    {title}
                  </IgTypography>
                ) : (
                  title
                )}
              </Grid>
              {message ? (
                <Grid item sx={{ marginTop: "auto", marginBottom: "auto" }}>
                  {typeof message === "string" ? (
                    <IgTypography>{message}</IgTypography>
                  ) : (
                    <span>{message}</span>
                  )}
                </Grid>
              ) : null}
            </Grid>
            {longMessage ? (
              <Grid item>
                {typeof longMessage === "string" ? (
                  <IgTypography> {longMessage}</IgTypography>
                ) : (
                  <span> {longMessage}</span>
                )}
              </Grid>
            ) : null}
          </Grid>
          {(dismissId && dispatch) || (buttons && buttons.length) ? (
            <Grid
              container
              item
              columnSpacing={1}
              alignItems={"center"}
              justifyContent={"flex-end"}
              sx={{
                maxWidth: "350px",
                flexWrap: "nowrap",
                marginTop: "auto",
                marginBottom: "auto",
              }}
            >
              {loadingPercent && loadingPercent > 0 ? (
                <IgTypography sx={{ color: ColorSecondary.light }}>
                  {loadingPercent.toFixed(0)}%
                </IgTypography>
              ) : null}
              {(buttons || []).map((button, index) => {
                return (
                  <Grid item key={`alert-button-${index}`}>
                    {button}
                  </Grid>
                );
              })}
              {!hideDismiss && dismissId ? (
                <Grid
                  item
                  key={"dismiss-alert"}
                  sx={{ marginTop: "auto", marginBottom: "auto" }}
                >
                  <IgCloseButton
                    onClick={() =>
                      dispatch(
                        dismissAlert({
                          orgId: org.data.org.id,
                          alertId: dismissId,
                        })
                      )
                    }
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : null}
        </Grid>
        <Grid
          item
          sx={{
            paddingTop: "8px",
            marginLeft: "-20px",
            marginRight: "-20px",
          }}
        >
          <Box
            className="indeterminate-progress-bar"
            sx={{
              height: "3px",
              opacity: loading ? 1 : 0,
              borderBottomLeftRadius: "15px",
              borderBottomRightRadius: "15px",
            }}
          >
            <Box
              sx={{
                backgroundColor: ColorMap[severity],
                height: "100%",
                width: `${loadingPercent}%`,
              }}
              className={
                loadingPercent === undefined
                  ? "indeterminate-progress-bar-value"
                  : undefined
              }
            />
          </Box>
        </Grid>
      </Grid>
    </HiddenErrorBoundary>
  );
};
